import PropTypes from 'prop-types'
import React, { useEffect, useState, useContext, useRef } from 'react'
import PaymentForm from 'components/bookings/PaymentForm'
import OrderPayWithGiftCardForm from 'components/bookings/OrderPayWithGiftCardForm'
import OrderCheckoutValidationCouponForm from 'components/bookings/OrderCheckoutValidationCouponForm'
import googleTracker from 'lib/trackers/googleTracker'
import CheckoutContext from 'components/bookings/contexts/CheckoutContext'
import ErrorModal from 'components/bookings/ErrorModal'
import NavigationButtons from './NavigationButtons'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { isEmpty } from 'lodash'

const stripePromise = loadStripe(process.env.STRIPE_PUBLISHABLE_KEY, {
  betas: ['blocked_card_brands_beta_2']
})

const OrderPayment = ({
  order,
  user,
  setOrder,
  urls,
  onOrderModification,
  trackingInfo,
  authenticityToken
}) => {
  const context = useContext(CheckoutContext)
  const [disableSubmitButton, setDisableSubmitButton] = useState(false)
  const [errors, setErrors] = useState([])
  const [clientSecret, setClientSecret] = useState('')

  const modalRef = useRef()
  const openModal = () => {
    modalRef.current.open()
  }
  const closeModal = () => {
    modalRef.current.close()
  }

  useEffect(() => {
    if (isEmpty(errors)) {
      closeModal()
    } else {
      openModal()
    }
  }, [errors])

  useEffect(() => {
    if (context.currentStep.name === 'Paiement') {
      fetch(urls.findOrCreatePaymentIntentURL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payable_id: order.id,
          payable_type: 'Order',
          authenticity_token: authenticityToken
        })
      })
        .then(async (response) => {
          if (response.status === 404) {
            const errorData = await response.json().catch(() => ({}))
            console.error('Order not found:', errorData)
            return null
          }
          if (!response.ok) {
            throw new Error(`${response.status}`)
          }
          return response.json()
        })
        .then((data) => {
          if (data.payment_intent) {
            setClientSecret(data.payment_intent.client_secret)
          }
        })
        .catch((error) => {
          console.error('Network or server error:', error)
        })
    }
  }, [clientSecret, order, context.currentStep])

  useEffect(() => {
    if (order.paymentErrorMessage) {
      setErrors([{ field: 'Erreur', message: order.paymentErrorMessage }])
    }
  }, [order])

  const finalizeCheckout = async () => {
    setDisableSubmitButton(true)

    if (trackingInfo) {
      googleTracker.trackAddPaymentInfo(trackingInfo)
    }

    setErrors([])
    context.startLoading?.()

    try {
      const response = await fetch(urls.finalizeOrderURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          order_id: order.id,
          authenticity_token: authenticityToken
        }),
        cache: 'no-store'
      })

      if (!response.ok) {
        const data = await response.json()
        setErrors([{ field: 'Erreur', message: data.error }])
        context.stopLoading?.()
      } else {
        const data = await response.json()
        context.stopLoading?.()
        setOrder(data.order)
      }
    } catch (error) {
      context.stopLoading?.()
      setErrors([])
    }
  }

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#123B82',
      colorText: '#333333',
      colorDanger: '#E02D3C',
      fontFamily: 'Ideal Sans, system-ui, sans-serif'
    }
  }
  const loader = 'auto'

  return (
    <div data-rspec="checkout-payment-container">
      <div className="step-title">Paiement</div>
      <div className="row mb-4">
        <div className="col-xs-12">
          <OrderPayWithGiftCardForm
            order={order}
            urls={urls}
            onOrderModification={onOrderModification}
            authenticityToken={authenticityToken}
          />
        </div>
      </div>
      <div className="row mb-4">
        <div className="col-xs-12">
          <OrderCheckoutValidationCouponForm
            order={order}
            onOrderModification={onOrderModification}
            urls={urls}
            authenticityToken={authenticityToken}
          />
        </div>
      </div>
      {order.paymentPolicy.leftToPayOnline > 0 ? (
        <div className="row">
          <div className="col-xs-12">
            {clientSecret && (
              <Elements
                options={{
                  clientSecret,
                  appearance,
                  loader,
                  disallowedCardBrands: ['american_express']
                }}
                stripe={stripePromise}>
                <PaymentForm
                  order={order}
                  user={user}
                  leftToPayWithStripe={order.paymentPolicy.leftToPayOnline}
                  setErrors={setErrors}
                  urls={urls}
                  authenticityToken={authenticityToken}
                />
              </Elements>
            )}
          </div>
        </div>
      ) : (
        <NavigationButtons
          priceAfter={order.priceAfter}
          goPrevious={context.goPrevious}
          submitLabel="Finaliser"
          handleValidate={finalizeCheckout}
          disableSubmitButton={disableSubmitButton}
        />
      )}

      <ErrorModal ref={modalRef} errors={errors} />
    </div>
  )
}

OrderPayment.propTypes = {
  order: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  setOrder: PropTypes.func.isRequired,
  urls: PropTypes.object.isRequired,
  onOrderModification: PropTypes.func,
  trackingInfo: PropTypes.object,
  authenticityToken: PropTypes.string.isRequired
}

export default OrderPayment
